import React, { useEffect, useState } from "react";
import Navbar from "../components/Navbar";
import { useAuth } from "../AuthContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import SingleCVScan from "../components/Dashboard_recruiter_components/SingleCVScan";
import CandidateSearch from "../components/Dashboard_recruiter_components/CandidateSearch";
import ApiAccess from "../components/Dashboard_recruiter_components/ApiAccess";
import RecruiterSidebar from "../components/layout/RecruiterSidebar";
import {
  Info,
  TrendingUp,
} from "lucide-react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
} from "recharts";

export default function RecruiterDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [activeAction, setActiveAction] = useState("dashboard");
  const [scans, setScans] = useState([]);
  const [topCandidates, setTopCandidates] = useState([]);
  const [dashboardStats, setDashboardStats] = useState({
    totalScans: 0,
    avgMatch: 0,
    shortlisted: 0,
    totalJobs: 0,
  });
  const [jobsMissingCriteria, setJobsMissingCriteria] = useState([]);
  const [selectedDashboardJobName, setSelectedDashboardJobName] = useState("");
  const [topCandidatesPage, setTopCandidatesPage] = useState(1);
  const [decisionCandidatesPage, setDecisionCandidatesPage] = useState(1);
  const [reviewCandidatesPage, setReviewCandidatesPage] = useState(1);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [note, setNote] = useState("");
  const requestedTool = searchParams.get("tool");

  const hasJobContext = (scan) =>
    Boolean(scan?.job_id || scan?.job_name?.trim?.() || scan?.job_description?.trim?.());

  const getJobLabel = (scan) =>
    scan?.job_name || scan?.job_description || "Unknown Job";

  const scanHasScoreBreakdown = (scan) =>
    Boolean(scan?.has_score_breakdown || scan?.scoring_breakdown);
  
  // Helper: Max scans based on plan
  const getMaxScans = (plan) => {
    switch (plan) {
      case "free": return 5;
      case "basic": return 200;
      case "pro": return 500;
      case "pilot": return 75;
      default: return 0;
    }
  };

  // Ensure maxScans is computed after user is available
  const maxScans = user ? getMaxScans(user.plan) : 0;
  const [scansLeft, setScansLeft] = useState(
    user ? (maxScans === null ? null : maxScans - (user.used_scans || 0)) : 0
  );

  // Inside your Panel 4
  const pieData = [
    { name: "Shortlisted", value: dashboardStats.shortlisted },
    { name: "Needs Review", value: Math.max(0, dashboardStats.totalScans - dashboardStats.shortlisted) },
  ];

  // Auth guard
  useEffect(() => {
    if (!loading && !user) navigate("/login");
  }, [user, loading, navigate]);

  useEffect(() => {
    const allowedTools = ["dashboard", "single", "candidate_search", "api"];

    if (!requestedTool) {
      setActiveAction("dashboard");
      return;
    }

    if (requestedTool === "bulk") {
      navigate("/bulk-scan", { replace: true });
      return;
    }

    if (requestedTool === "single" && user?.plan !== "free") {
      navigate("/bulk-scan", { replace: true });
      return;
    }

    if (allowedTools.includes(requestedTool)) {
      setActiveAction(requestedTool);
    }
  }, [requestedTool, navigate, user?.plan]);

  // Fetch dashboard
  useEffect(() => {
    if (!user) return;
    const fetchDashboard = async () => {
      try {
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/dashboard/my_scans/?user_id=${user.id}`,
          {
            credentials: "include",
          }
        );
        const data = await res.json();
        setScans(data.scans || []);
        setTopCandidates(data.top_candidates || []);
        setDashboardStats({
          totalScans: data.total_scans || 0,
          avgMatch: data.avg_match || 0,
          shortlisted: data.shortlisted || 0,
          totalJobs: data.total_jobs || 0,
        });
        setJobsMissingCriteria(data.jobs_missing_criteria || []);

        // Calculate scans left (backend or fallback)
        const remaining =
          data.scans_left !== undefined
            ? data.scans_left
            : maxScans === null
              ? null
              : maxScans - (user.used_scans || 0);
        setScansLeft(remaining);

        // Auto-expand first job
      } catch (err) {
        console.error("Dashboard fetch error:", err);
      }
    };
    fetchDashboard();
  }, [user]);
  
  useEffect(() => {
    if (scans.length > 0) {
      // Remove duplicates (same candidate)
      const uniqueCandidates = Array.from(
        new Map(scans.filter(hasJobContext).map(s => [s.file_name, s])).values()
      );

      // Sort by match score (highest first)
      const sorted = uniqueCandidates.sort(
        (a, b) => b.match_score - a.match_score
      );

      setTopCandidates(sorted);
    }
  }, [scans]);

  if (loading || !user) return <p className="text-center mt-20">Loading...</p>;

  const jobScans = scans.filter(hasJobContext);

  const groupedJobs = jobScans.reduce((acc, scan) => {
    const jobName = getJobLabel(scan);
    if (!acc[jobName]) acc[jobName] = [];
    acc[jobName].push(scan);
    return acc;
  }, {});

  // Map candidate ID → array of job names
  const candidateJobsMap = scans.reduce((acc, scan) => {
    if (!hasJobContext(scan)) return acc;
    if (!acc[scan.candidate_id]) acc[scan.candidate_id] = new Set();
    acc[scan.candidate_id].add(getJobLabel(scan));
    return acc;
  }, {});
  
  const barData = Object.entries(groupedJobs)
    .map(([jobName, scansForJob]) => {
      const shortlisted = scansForJob.filter(s => s.is_shortlisted).length;
      const total = scansForJob.length;

      return {
        job: jobName,
        displayJob: jobName.length > 22 ? `${jobName.slice(0, 22)}...` : jobName,
        shortlisted,
        nonSelected: total - shortlisted,
        total,
      };
    })
    .sort((a, b) => b.total - a.total)
    .slice(0, 7);

  const matchQualityData = [
    {
      label: "Strong fit",
      helper: "70% and above",
      value: jobScans.filter(scan => Number(scan.match_score || 0) >= 70).length,
      bar: "bg-emerald-500",
      text: "text-emerald-700",
    },
    {
      label: "Moderate fit",
      helper: "40% to 69%",
      value: jobScans.filter(scan => {
        const score = Number(scan.match_score || 0);
        return score >= 40 && score < 70;
      }).length,
      bar: "bg-amber-500",
      text: "text-amber-700",
    },
    {
      label: "Low fit",
      helper: "Below 40%",
      value: jobScans.filter(scan => Number(scan.match_score || 0) < 40).length,
      bar: "bg-rose-500",
      text: "text-rose-700",
    },
  ];

  const isFree = user.plan === "free";
  const isPro = user.plan === "pro";
  const isPilot = user.plan === "pilot";

  const quotaExceeded = scansLeft !== null && scansLeft <= 0;
  const shortlistedRate = dashboardStats.totalScans
    ? Math.round((dashboardStats.shortlisted / dashboardStats.totalScans) * 100)
    : 0;

  const getScoreBarClass = (score = 0) => {
    if (score >= 80) return "bg-emerald-500";
    if (score >= 60) return "bg-sky-500";
    if (score >= 40) return "bg-amber-500";
    return "bg-rose-500";
  };

  const MetricInfo = ({ text, dark = false }) => (
    <span className="group relative inline-flex align-middle">
      <Info
        size={14}
        className={`cursor-help transition ${
          dark ? "text-emerald-100/80 group-hover:text-white" : "text-slate-400 group-hover:text-slate-700"
        }`}
        aria-label={text}
      />
      <span className={`pointer-events-none absolute left-1/2 top-5 z-30 hidden w-64 -translate-x-1/2 rounded-lg px-3 py-2 text-left text-xs font-medium leading-5 shadow-lg group-hover:block ${
        dark
          ? "border border-emerald-300/30 bg-white text-slate-800"
          : "border border-slate-200 bg-white text-slate-600"
      }`}>
        {text}
      </span>
    </span>
  );

  const DashboardLayer = ({ eyebrow, title, description, children }) => (
    <section className="mb-6 rounded-xl border border-emerald-100 bg-emerald-50/35 p-3 shadow-sm">
      <div className="mb-3 flex flex-col gap-1 px-1 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.18em] text-emerald-700">{eyebrow}</p>
          <h2 className="mt-1 text-lg font-semibold tracking-tight text-slate-950">{title}</h2>
          <p className="mt-1 max-w-4xl text-sm leading-6 text-slate-500">{description}</p>
        </div>
      </div>
      {children}
    </section>
  );

  const sidebarSummaryStats = [
    { label: "CVs analyzed", value: dashboardStats.totalScans },
    { label: "Avg match", value: `${dashboardStats.avgMatch}%` },
    { label: "Shortlisted", value: dashboardStats.shortlisted },
    { label: "Scans left", value: scansLeft },
  ];

  const strongMatches = jobScans.filter(scan => Number(scan.match_score || 0) >= 70);
  const reviewMatches = jobScans.filter(scan => {
    const score = Number(scan.match_score || 0);
    return score >= 40 && score < 70;
  });
  const riskMatches = jobScans.filter(scan => Number(scan.match_score || 0) < 40);
  const readyToShortlist = strongMatches.filter(scan => !scan.is_shortlisted).length;
  const jobsNeedingReview = Object.values(groupedJobs).filter(scansForJob =>
    scansForJob.some(scan => Number(scan.match_score || 0) >= 70 && !scan.is_shortlisted)
  ).length;
  const candidatesWithSkillGaps = jobScans.filter(scan => (scan.missing_skills || []).length > 0).length;
  const jobsMissingScoreBreakdown = Object.entries(groupedJobs)
    .map(([jobName, scansForJob]) => {
      const uniqueScans = Array.from(new Map(scansForJob.map(scan => [scan.scan_id, scan])).values());
      const missingCount = uniqueScans.filter(scan => !scanHasScoreBreakdown(scan)).length;
      const jobId = uniqueScans.find(scan => scan.job_id)?.job_id;

      return {
        jobName,
        jobId,
        missingCount,
        totalCount: uniqueScans.length,
      };
    })
    .filter(job => job.missingCount > 0)
    .sort((a, b) => b.missingCount - a.missingCount);
  const scoreClarityJob = jobsMissingScoreBreakdown[0];
  const criteriaSetupJob = jobsMissingCriteria[0];
  const strongestUnreviewed = [...strongMatches]
    .filter(scan => !scan.is_shortlisted)
    .sort((a, b) => Number(b.match_score || 0) - Number(a.match_score || 0))[0];
  const mostRecentScan = [...jobScans].sort(
    (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
  )[0];
  const nextBestAction = strongestUnreviewed
    ? `Review ${strongestUnreviewed.file_name} for ${getJobLabel(strongestUnreviewed)}.`
    : criteriaSetupJob
      ? `${criteriaSetupJob.job_name} needs scoring criteria for more accurate ranking.`
    : scoreClarityJob
      ? `${scoreClarityJob.jobName} has ${scoreClarityJob.missingCount} candidate${scoreClarityJob.missingCount === 1 ? "" : "s"} without score explanation.`
    : jobsNeedingReview > 0
      ? "Open job rankings and review strong candidates."
      : jobScans.length > 0
        ? "Review skill gaps and refine job criteria or sourcing."
        : "Create a job and scan your first CV batch.";
  const dashboardPriorityCards = [
    {
      label: "Next best action",
      value: strongestUnreviewed ? `${Math.round(strongestUnreviewed.match_score || 0)}%` : jobScans.length ? `${dashboardStats.avgMatch}%` : "Start",
      helper: nextBestAction,
      tone: "border-emerald-300 bg-emerald-50 text-emerald-950",
      cta: strongestUnreviewed ? "Open candidate" : jobScans.length ? "Open rankings" : "Create job",
      onClick: () => {
        if (strongestUnreviewed) {
          handleOpenCandidate(strongestUnreviewed);
        } else if (jobScans.length) {
          navigate("/rankings");
        } else {
          navigate("/jobs");
        }
      },
    },
    {
      label: "Criteria needed",
      value: jobsMissingCriteria.length || "OK",
      helper: criteriaSetupJob
        ? `${criteriaSetupJob.job_name} has no must-have or preferred criteria yet. Add criteria to improve scoring accuracy.`
        : "All open jobs have recruiter scoring criteria.",
      tone: criteriaSetupJob
        ? "border-rose-400 bg-rose-100 text-rose-950 ring-2 ring-rose-200"
        : "border-emerald-300 bg-emerald-50 text-emerald-950",
      warning: Boolean(criteriaSetupJob),
      cta: criteriaSetupJob?.job_id ? "Add criteria" : "View jobs",
      onClick: () => {
        if (criteriaSetupJob?.job_id) {
          navigate(`/jobs/${criteriaSetupJob.job_id}`);
        } else {
          navigate("/jobs");
        }
      },
    },
    {
      label: "Score clarity",
      value: scoreClarityJob ? jobsMissingScoreBreakdown.length : "OK",
      helper: scoreClarityJob
        ? `${scoreClarityJob.jobName}: ${scoreClarityJob.missingCount}/${scoreClarityJob.totalCount} scanned candidates need full score explanation.`
        : "All recent job scans include score explanations.",
      tone: scoreClarityJob
        ? "border-rose-400 bg-rose-100 text-rose-950 ring-2 ring-rose-200"
        : "border-emerald-300 bg-emerald-50 text-emerald-950",
      warning: Boolean(scoreClarityJob),
      cta: scoreClarityJob?.jobId ? "Open job" : "View rankings",
      onClick: () => {
        if (scoreClarityJob?.jobId) {
          navigate(`/jobs/${scoreClarityJob.jobId}`);
        } else {
          navigate("/rankings");
        }
      },
    },
    {
      label: "Shortlist opportunity",
      value: readyToShortlist,
      helper: "Strong candidates waiting for recruiter decision.",
      tone: "border-sky-200 bg-sky-50 text-sky-950",
      cta: "Review rankings",
      onClick: () => navigate("/rankings"),
    },
    {
      label: "Gap pressure",
      value: candidatesWithSkillGaps,
      helper: "Candidates show missing requirement signals across active scans.",
      tone: "border-amber-200 bg-amber-50 text-amber-950",
      cta: "Inspect gaps",
      onClick: () => navigate("/rankings"),
    },
  ];

  const hiringFocus = [
    {
      label: "Ready to shortlist",
      value: readyToShortlist,
      helper: "Strong matches not shortlisted yet",
      tone: "bg-emerald-50 text-emerald-800 border-emerald-100",
    },
    {
      label: "Jobs needing review",
      value: jobsNeedingReview,
      helper: "Roles with strong unreviewed candidates",
      tone: "bg-sky-50 text-sky-800 border-sky-100",
    },
    {
      label: "Skill gaps detected",
      value: candidatesWithSkillGaps,
      helper: "Candidates with missing requirement signals",
      tone: "bg-amber-50 text-amber-800 border-amber-100",
    },
  ];

  const radarColumns = [
    {
      label: "Strong Matches",
      helper: "70% and above",
      scans: strongMatches,
      border: "border-emerald-200",
      bg: "bg-emerald-50",
      dot: "bg-emerald-600",
    },
    {
      label: "Needs Review",
      helper: "40% to 69%",
      scans: reviewMatches,
      border: "border-amber-200",
      bg: "bg-amber-50",
      dot: "bg-amber-600",
    },
    {
      label: "Gaps / Risk",
      helper: "Below 40%",
      scans: riskMatches,
      border: "border-rose-200",
      bg: "bg-rose-50",
      dot: "bg-rose-600",
    },
  ];

  const funnelData = [
    { label: "Scanned", value: dashboardStats.totalScans, color: "bg-slate-900" },
    { label: "Strong fit", value: strongMatches.length, color: "bg-emerald-600" },
    { label: "AI reviewed", value: topCandidates.length, color: "bg-sky-600" },
    { label: "Shortlisted", value: dashboardStats.shortlisted, color: "bg-amber-600" },
  ];

  const sortByScore = (items) =>
    [...items].sort((a, b) => Number(b.match_score || 0) - Number(a.match_score || 0));

  const jobHealthCards = Object.entries(groupedJobs)
    .map(([jobName, scansForJob]) => {
      const uniqueScans = Array.from(new Map(scansForJob.map(scan => [scan.scan_id, scan])).values());
      const topScan = [...uniqueScans].sort((a, b) => (b.match_score || 0) - (a.match_score || 0))[0];
      const latestScan = [...uniqueScans].sort(
        (a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0)
      )[0];
      const avgScore = uniqueScans.length
        ? Math.round(uniqueScans.reduce((sum, scan) => sum + (scan.match_score || 0), 0) / uniqueScans.length)
        : 0;
      const gapCount = uniqueScans.reduce((sum, scan) => sum + ((scan.missing_skills || []).length ? 1 : 0), 0);
      const jobId = uniqueScans.find(scan => scan.job_id)?.job_id;
      const shortlistedCount = uniqueScans.filter(scan => scan.is_shortlisted).length;
      const rejectedCount = uniqueScans.filter(scan => scan.is_rejected).length;
      const shortlistedCandidates = sortByScore(uniqueScans.filter(scan => scan.is_shortlisted));
      const rejectedCandidates = sortByScore(uniqueScans.filter(scan => scan.is_rejected));
      const reviewCandidates = sortByScore(uniqueScans.filter(scan => !scan.is_shortlisted && !scan.is_rejected));
      const topScore = Math.round(topScan?.match_score || 0);
      const strongMatches = uniqueScans.filter(scan => Number(scan.match_score || 0) >= 70).length;
      const missingSkillCounts = uniqueScans.reduce((acc, scan) => {
        (scan.missing_skills || []).forEach(skill => {
          acc[skill] = (acc[skill] || 0) + 1;
        });
        return acc;
      }, {});
      const topMissingSkill = Object.entries(missingSkillCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
      const topImpact = shortlistedCount > 0
        ? "Shortlist progress"
        : topMissingSkill
          ? topMissingSkill
          : topScore >= 70
            ? "Strong match"
            : "Criteria needed";
      const reviewedProgress = shortlistedCount > 0;
      const risk =
        reviewedProgress || topScore >= 75
          ? "Low"
          : strongMatches > 0 || avgScore >= 45
          ? "Medium"
          : "High";
      const stages = [
        {
          label: "Job ready",
          detail: "Requirements analyzed",
          complete: true,
        },
        {
          label: "CVs received",
          detail: `${uniqueScans.length} applicant${uniqueScans.length === 1 ? "" : "s"}`,
          complete: uniqueScans.length > 0,
        },
        {
          label: "Scanned",
          detail: `${uniqueScans.length} CV${uniqueScans.length === 1 ? "" : "s"} scored`,
          complete: uniqueScans.length > 0,
        },
        {
          label: "Shortlisted",
          detail: `${shortlistedCount} selected`,
          complete: shortlistedCount > 0,
        },
      ];
      const nextStageIndex = stages.findIndex(stage => !stage.complete);

      return {
        jobName,
        jobId,
        applicants: uniqueScans.length,
        shortlistedCount,
        rejectedCount,
        shortlistedCandidates,
        rejectedCandidates,
        reviewCandidates,
        bestMatchName: topScan?.file_name || "No candidates yet",
        latestActivityAt: latestScan?.created_at,
        topScore,
        avgScore,
        gapCount,
        topImpact,
        risk,
        stages,
        nextStageIndex,
      };
    })
    .sort((a, b) => b.topScore - a.topScore)
    .slice(0, 4);
  const selectedJobHealthCards = jobHealthCards.length
    ? [jobHealthCards.find(job => job.jobName === selectedDashboardJobName) || jobHealthCards[0]]
    : [];
  const selectedDashboardJobValue =
    selectedDashboardJobName && jobHealthCards.some(job => job.jobName === selectedDashboardJobName)
      ? selectedDashboardJobName
      : jobHealthCards[0]?.jobName || "";

  const commonMissingSkills = Object.entries(
    jobScans.reduce((acc, scan) => {
      (scan.missing_skills || []).forEach(skill => {
        acc[skill] = (acc[skill] || 0) + 1;
      });
      return acc;
    }, {})
  )
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  const aiFindings = [
    readyToShortlist > 0
      ? `${readyToShortlist} strong candidate${readyToShortlist === 1 ? "" : "s"} can be reviewed for shortlist now.`
      : "No unreviewed strong matches are waiting right now.",
    commonMissingSkills.length > 0
      ? `${commonMissingSkills[0][0]} is the most frequent gap across recent scans.`
      : "No repeated skill gap pattern is visible yet.",
    dashboardStats.avgMatch >= 60
      ? "Current candidate quality is trending healthy for active roles."
      : "Average match is still low, so job criteria or sourcing may need refinement.",
  ];

  const highestGapJob = [...jobHealthCards].sort((a, b) => b.gapCount - a.gapCount)[0];
  const gapPressurePercent = jobScans.length
    ? Math.round((candidatesWithSkillGaps / jobScans.length) * 100)
    : 0;

  const recruiterCriteriaImpact = [
    {
      label: "Biggest blocker",
      value: commonMissingSkills.length > 0 ? commonMissingSkills[0][0] : "Clear",
      detail: commonMissingSkills.length > 0
        ? `${commonMissingSkills[0][1]} candidate${commonMissingSkills[0][1] === 1 ? "" : "s"} missed this requirement signal across recent scans.`
        : "No repeated requirement blocker is visible in recent scan results.",
      helper: "This tells the recruiter which requirement is reducing match quality most often.",
      tone: "border-emerald-200 bg-emerald-50 text-emerald-900",
    },
    {
      label: "Criteria pressure",
      value: `${gapPressurePercent}%`,
      detail: jobScans.length > 0
        ? `${candidatesWithSkillGaps} of ${jobScans.length} scanned candidate${jobScans.length === 1 ? "" : "s"} show at least one missing requirement signal.`
        : "Scan candidates to see how strict the current requirements are.",
      helper: "Higher pressure means the JD may be too narrow, or sourcing may need adjustment.",
      tone: "border-sky-200 bg-sky-50 text-sky-900",
    },
    {
      label: "Most affected job",
      value: highestGapJob?.jobName || "None yet",
      detail: highestGapJob
        ? `${highestGapJob.gapCount} candidate${highestGapJob.gapCount === 1 ? "" : "s"} show missing requirement signals for this role.`
        : "No job has enough scan history to measure criteria impact yet.",
      helper: "Use this when deciding whether to refine criteria or import more candidates.",
      tone: "border-amber-200 bg-amber-50 text-amber-900",
    },
  ];

  const headerContent = {
    dashboard: {
      eyebrow: "Recruitment overview",
      title: "Dashboard",
      subtitle: `Welcome, ${user.first_name || "Recruiter"}. Monitor CV screening, compare candidate quality, and move faster on the strongest matches.`,
    },
    single: {
      eyebrow: "CV analysis",
      title: "Single CV Scan",
      subtitle: "Analyze one candidate CV against a job description.",
    },
    candidate_search: {
      eyebrow: "Talent intelligence",
      title: "AI Talent Search",
      subtitle: "Search your candidate database using role requirements and semantic matching.",
    },
    api: {
      eyebrow: "Developer access",
      title: "API Access",
      subtitle: "Create API keys, review usage history, and connect external tools.",
    },
  };

  const activeHeader = headerContent[activeAction] || headerContent.dashboard;

  const handleOpenCandidate = (scan) => {
    setSelectedCandidate(scan);
    setNote(scan.recruiter_note || "");
  };

  const handleSaveNote = async () => {
    try {
      await fetch(
        `${process.env.REACT_APP_API_URL}/dashboard/my_scans/${selectedCandidate.scan_id}/note`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            note: note, // matches backend payload
          }),
        }
      );

      // Update UI instantly
      setScans(prev =>
        prev.map(s =>
          s.scan_id === selectedCandidate.scan_id
            ? { ...s, recruiter_note: note }
            : s
        )
      );

      setSelectedCandidate(null);

    } catch (err) {
      console.error("Failed to save note", err);
    }
  };

  // Handle shorlisting candidates
  const syncCandidateDecision = (scanId, updates) => {
    setScans(prev =>
      prev.map(scan =>
        scan.scan_id === scanId ? { ...scan, ...updates } : scan
      )
    );
    setTopCandidates(prev =>
      prev.map(scan =>
        scan.scan_id === scanId ? { ...scan, ...updates } : scan
      )
    );
    setSelectedCandidate(prev =>
      prev?.scan_id === scanId ? { ...prev, ...updates } : prev
    );
  };

  const applyShortlistStatChange = (scan, wasShortlisted, isShortlisted) => {
    if (wasShortlisted === isShortlisted || !hasJobContext(scan)) return;

    setDashboardStats(prev => ({
      ...prev,
      shortlisted: Math.max(0, prev.shortlisted + (isShortlisted ? 1 : -1)),
    }));
  };

  const handleQuickShortlist = async (scan) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/dashboard/my_scans/${scan.scan_id}/shortlist`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      const data = await res.json();
      syncCandidateDecision(scan.scan_id, {
        is_shortlisted: data.is_shortlisted,
        is_rejected: data.is_rejected,
      });
      applyShortlistStatChange(scan, scan.is_shortlisted, data.is_shortlisted);
    } catch (err) {
      console.error("Shortlist toggle failed", err);
    }
  };

  const handleQuickReject = async (scan) => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/dashboard/my_scans/${scan.scan_id}/reject`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      const data = await res.json();
      syncCandidateDecision(scan.scan_id, {
        is_shortlisted: data.is_shortlisted,
        is_rejected: data.is_rejected,
      });
      applyShortlistStatChange(scan, scan.is_shortlisted, data.is_shortlisted);
    } catch (err) {
      console.error("Reject toggle failed", err);
    }
  };

  const handleToggleShortlist = async () => {
    try {
      if (note !== (selectedCandidate.recruiter_note || "")) {
        await fetch(
          `${process.env.REACT_APP_API_URL}/dashboard/my_scans/${selectedCandidate.scan_id}/note`,
          {
            method: "PUT",
            credentials: "include",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({
              note: note,
            }),
          }
        );
      }

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/dashboard/my_scans/${selectedCandidate.scan_id}/shortlist`,
        {
          method: "POST",
          credentials: "include",
        }
      );

      const data = await res.json();
      const wasShortlisted = selectedCandidate.is_shortlisted;

      syncCandidateDecision(selectedCandidate.scan_id, {
        is_shortlisted: data.is_shortlisted,
        is_rejected: data.is_rejected,
        recruiter_note: note,
      });
      applyShortlistStatChange(selectedCandidate, wasShortlisted, data.is_shortlisted);

    } catch (err) {
      console.error("Shortlist toggle failed", err);
    }
  };

  const handleScanConsumed = (count = 1) => {
    setScansLeft(prev =>
      prev === null ? null : Math.max(0, prev - count)
    );
  };

  const renderRecruiterCriteriaImpactPanel = () => (
    <section className="flex h-full flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-100 p-4 sm:p-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">Criteria impact</p>
            <h3 className="mt-1.5 flex items-center gap-2 text-lg font-semibold tracking-tight text-slate-950">
              <span>Recruiter Criteria Impact</span>
              <MetricInfo text="This explains how job requirements are affecting scan results, using repeated missing skills, candidate gap pressure, and job-level gap signals." />
            </h3>
            <p className="mt-1 text-sm leading-5 text-slate-500">
              See which requirements are shaping rankings before changing the JD or sourcing more candidates.
            </p>
          </div>
          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
            Explainable
          </span>
        </div>
      </div>

      <div className="flex flex-1 p-4 sm:p-5">
        <div className="grid flex-1 gap-2.5">
          {recruiterCriteriaImpact.map((item) => (
            <div
              key={item.label}
              className={`flex flex-col justify-between rounded-lg border px-3 py-2.5 text-left ${item.tone}`}
            >
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-semibold uppercase tracking-wide opacity-70">{item.label}</p>
                  <p className="mt-1 text-sm leading-5 opacity-85">{item.detail}</p>
                </div>
                <p className="shrink-0 rounded-md border border-current/10 bg-white/60 px-2.5 py-1 text-sm font-bold tabular-nums">
                  {item.value}
                </p>
              </div>
              <p className="mt-2 rounded-md border border-current/10 bg-white/55 px-2.5 py-1.5 text-xs leading-5 opacity-80">
                {item.helper}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );

  const paginateItems = (items = [], page = 1, perPage = 5) => {
    const totalPages = Math.max(1, Math.ceil(items.length / perPage));
    const safePage = Math.min(Math.max(page, 1), totalPages);
    const start = (safePage - 1) * perPage;
    return {
      items: items.slice(start, start + perPage),
      page: safePage,
      totalPages,
    };
  };

  const PaginationControls = ({ page, totalPages, onChange }) => {
    if (totalPages <= 1) return null;

    const changePage = (nextPage) => {
      const scrollX = window.scrollX;
      const scrollY = window.scrollY;
      onChange(nextPage);

      const restoreScroll = () => {
        window.scrollTo({ left: scrollX, top: scrollY, behavior: "auto" });
      };

      window.requestAnimationFrame(restoreScroll);
      window.setTimeout(restoreScroll, 0);
    };

    return (
      <div className="mt-3 flex items-center justify-between gap-2 border-t border-slate-100 pt-3 text-xs">
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            changePage(Math.max(1, page - 1));
          }}
          disabled={page <= 1}
          className="rounded-md border border-slate-200 bg-white px-2.5 py-1 font-semibold text-slate-600 transition hover:border-emerald-300 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Prev
        </button>
        <span className="font-semibold text-slate-500">
          Page {page} of {totalPages}
        </span>
        <button
          type="button"
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            changePage(Math.min(totalPages, page + 1));
          }}
          disabled={page >= totalPages}
          className="rounded-md border border-slate-200 bg-white px-2.5 py-1 font-semibold text-slate-600 transition hover:border-emerald-300 hover:text-emerald-700 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Next
        </button>
      </div>
    );
  };

  const renderTopCandidatesPanel = () => (
    (() => {
      const uniqueTopCandidates = Array.from(new Map((topCandidates || []).map(c => [c.scan_id, c])).values());
      const pagedTopCandidates = paginateItems(uniqueTopCandidates, topCandidatesPage, 4);

      return (
        <section className="flex h-full flex-col rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
          <div className="mb-3 flex items-start justify-between gap-3">
            <div>
              <h3 className="text-xl font-semibold tracking-tight text-slate-950">Top Candidates</h3>
              <p className="mt-1 text-sm text-slate-500">Highest scoring candidates across recent scans</p>
            </div>
            {uniqueTopCandidates.length > 0 && (
              <span className="rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs font-semibold text-slate-600">
                {uniqueTopCandidates.length}
              </span>
            )}
          </div>

          <div className="flex flex-1 flex-col gap-2 overflow-y-auto pr-1">
            {pagedTopCandidates.items.map((c, index) => {
          const candidateJobs = Array.from(candidateJobsMap[c.id] || []);
          const rank = (pagedTopCandidates.page - 1) * 4 + index + 1;

          return (
            <div
              key={c.scan_id}
              className={`flex items-start gap-2.5 rounded-lg border p-2.5 transition hover:border-slate-300 hover:shadow-sm ${
                c.is_shortlisted ? "border-emerald-300 bg-emerald-50" : "border-slate-200 bg-white"
              }`}
            >
              <div
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                  rank === 1
                    ? "bg-emerald-800 text-white"
                    : rank === 2
                    ? "bg-slate-200 text-slate-800"
                    : rank === 3
                    ? "bg-sky-100 text-sky-800"
                    : "bg-slate-100 text-slate-700"
                }`}
              >
                #{rank}
              </div>

              <div className="min-w-0 flex-1">
                <div className="mb-1 grid grid-cols-[minmax(0,1fr)_56px] items-center gap-2">
                  <span className="truncate text-sm font-semibold text-slate-800">
                    {c.file_name?.replace(".pdf", "").replace(/_/g, " ") || "Candidate"}
                  </span>
                  <span className="text-right text-sm font-semibold tabular-nums text-slate-950">{c.match_score}%</span>
                </div>
                <div className="mb-1.5 h-1.5 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={`h-full rounded-full ${getScoreBarClass(c.match_score)}`}
                    style={{ width: `${Math.max(0, Math.min(100, c.match_score || 0))}%` }}
                  />
                </div>

                {candidateJobs.length > 0 && (
                  <div className="mb-1 truncate text-[11px] text-slate-500">
                    <span className="font-medium text-slate-600">Roles: </span>
                    {candidateJobs.join(", ")}
                  </div>
                )}

                {c.matching_skills && c.matching_skills.length > 0 && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    {c.matching_skills.slice(0, 3).map((matching_skills, idx) => (
                      <span
                        key={idx}
                        className="rounded-full border border-slate-200 bg-slate-100 px-1.5 py-0.5 text-[11px] text-slate-700"
                      >
                        {matching_skills}
                      </span>
                    ))}
                    {c.matching_skills.length > 3 && (
                      <span className="rounded-full bg-slate-50 px-1.5 py-0.5 text-[11px] font-semibold text-slate-500">
                        +{c.matching_skills.length - 3}
                      </span>
                    )}
                  </div>
                )}

                {c.is_shortlisted && (
                  <span className="mt-1 inline-block text-[11px] font-semibold text-emerald-700">Shortlisted</span>
                )}
              </div>
            </div>
          );
            })}

            {uniqueTopCandidates.length === 0 && (
              <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-500">
                No candidates available yet.
              </p>
            )}
          </div>

          <PaginationControls
            page={pagedTopCandidates.page}
            totalPages={pagedTopCandidates.totalPages}
            onChange={setTopCandidatesPage}
          />
        </section>
      );
    })()
  );

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900">
      <Navbar />

      {/* Main Content */}
      <div className="px-4 pt-20 pb-8 sm:px-6 lg:px-0 lg:pb-0 lg:pt-16">
        <div
          className="mx-auto grid w-full max-w-[1760px] gap-6 lg:gap-0 lg:grid-cols-[16rem_minmax(0,1fr)]"
        >
        {/* Left Nav */}
        <aside className="w-full self-start lg:sticky lg:top-16 lg:h-[calc(100vh-4rem)] lg:bg-[#03140f] lg:p-4">
          <RecruiterSidebar
            activeItem={activeAction}
            onToolSelect={setActiveAction}
            user={user}
            scansLeft={scansLeft}
            summaryStats={activeAction === "dashboard" ? sidebarSummaryStats : null}
          />
        </aside>

        {/* Dashboard Panels */}
        <main
          className="flex min-w-0 flex-col gap-6 lg:px-8 lg:pb-8 lg:pt-5"
        >
          <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex min-w-0 flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
              <div className="min-w-0">
                <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <TrendingUp size={14} />
                  {activeHeader.eyebrow}
                </div>
                <h1 className="text-2xl font-semibold tracking-tight text-slate-950 md:text-3xl">
                  {activeHeader.title}
                </h1>
                <p className="mt-2 max-w-4xl text-slate-500">
                  {activeHeader.subtitle}
                </p>
              </div>

              <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:flex-wrap sm:items-center lg:justify-end">
                <span className="inline-flex min-h-[42px] items-center justify-center rounded-lg border border-slate-200 bg-white px-4 text-sm font-semibold text-slate-700">
                  {`Scans left: ${scansLeft}`}
                  <span className="ml-2">
                    <MetricInfo text="Remaining scan quota for the current billing period. New candidate-job scans and on-demand AI analysis can reduce this count." />
                  </span>
                </span>

                {isFree && (
                  <button
                    onClick={() => navigate("/profile?section=upgrade")}
                    className="inline-flex min-h-[42px] items-center justify-center rounded-lg bg-emerald-800 px-5 text-sm font-semibold text-white transition hover:bg-emerald-900"
                  >
                    Upgrade
                  </button>
                )}
              </div>
            </div>
          </section>

          {activeAction === "dashboard" && (
            <div className="w-full">
              <DashboardLayer
                eyebrow="Attention"
                title="What needs attention"
                description="Start here. RecruitGlance turns the workspace into the next recruiter action instead of another metrics wall."
              >
                <section className="overflow-hidden rounded-lg border border-emerald-900 bg-[#03140f] text-white shadow-sm">
                  <div className="grid gap-0 xl:grid-cols-[minmax(0,1fr)_390px]">
                    <div className="p-6 lg:p-7">
                      <div className="mb-5 flex flex-wrap items-center gap-2">
                        <span className="rounded-full border border-emerald-700 bg-emerald-950 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-emerald-100">
                          Recruiter priorities
                        </span>
                        <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs font-semibold text-emerald-100">
                          Action-first dashboard
                        </span>
                      </div>
                      <h2 className="max-w-3xl text-2xl font-semibold tracking-tight md:text-3xl">
                        Start with the decision that moves hiring forward.
                      </h2>
                      <p className="mt-3 max-w-3xl text-sm leading-6 text-emerald-50/75">
                        This panel turns scan history into recruiter actions: who to review, where candidates are weak, and which job needs attention next.
                      </p>
                      <div className="mt-6 grid gap-3 md:grid-cols-2 xl:grid-cols-5">
                        {dashboardPriorityCards.map((item) => (
                          <div key={item.label} className={`flex h-full flex-col rounded-lg border p-4 ${item.tone}`}>
                            <div className="min-h-[112px]">
                              <p className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide opacity-75">
                                {item.label}
                                <MetricInfo
                                  dark
                                  text={
                                    item.label === "Next best action"
                                      ? "Calculated from the strongest unreviewed candidate, jobs with strong pending candidates, and repeated skill gaps."
                                      : item.label === "Criteria needed"
                                      ? "Shows open jobs where must-have and preferred scoring criteria are not configured yet."
                                      : item.label === "Score clarity"
                                      ? "Shows jobs where scanned candidates do not yet have the full score explanation visible to recruiters."
                                      : item.label === "Shortlist opportunity"
                                      ? "Counts candidates scoring 70% or higher that have not been shortlisted yet."
                                      : "Counts scanned candidates where at least one job requirement was detected as missing."
                                  }
                                />
                              </p>
                              <p className={`mt-2 w-fit rounded-full px-3 py-1 text-3xl font-semibold tabular-nums ${
                                item.warning ? "bg-rose-600 text-white shadow-sm" : ""
                              }`}>
                                {item.value}
                              </p>
                              <p className="mt-2 text-sm leading-5 opacity-80">{item.helper}</p>
                            </div>
                            <button
                              type="button"
                              onClick={item.onClick}
                              className={`mt-auto inline-flex min-h-[34px] w-fit items-center justify-center rounded-md px-3 text-xs font-bold shadow-sm transition ${
                                item.warning
                                  ? "bg-rose-700 text-white hover:bg-rose-800"
                                  : "bg-white text-emerald-950 hover:bg-slate-50"
                              }`}
                            >
                              {item.cta}
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="border-t border-white/10 bg-white/[0.03] p-6 xl:border-l xl:border-t-0">
                      <p className="text-sm font-semibold uppercase tracking-wide text-emerald-100">Recruiter Signals</p>
                      <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.06] p-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-200/70">Latest scan context</p>
                        <p className="mt-2 truncate text-sm font-semibold text-white">
                          {mostRecentScan ? getJobLabel(mostRecentScan) : "No scans yet"}
                        </p>
                        <p className="mt-1 truncate text-xs text-emerald-50/70">
                          {mostRecentScan ? mostRecentScan.file_name : "Scan candidates to generate live decision signals."}
                        </p>
                      </div>
                      <div className="mt-4 space-y-3">
                        {aiFindings.map((finding, index) => (
                          <div key={`${finding}-${index}`} className="rounded-lg border border-white/10 bg-white/[0.06] p-3">
                            <p className="text-sm leading-6 text-emerald-50">{finding}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </section>
              </DashboardLayer>

              <DashboardLayer
                eyebrow="Screening"
                title="Screening performance"
                description="See whether the current candidate pool is strong, weak, or still needs recruiter review."
              >
              <div className="grid gap-6 xl:grid-cols-[1.15fr_0.85fr]">
                <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
                  <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <h3 className="flex items-center gap-2 text-xl font-semibold tracking-tight text-slate-950">
                        Hiring Radar
                        <MetricInfo text="Groups recent job-linked scans by match score: 70%+ strong, 40-69% needs review, below 40% higher risk." />
                      </h3>
                      <p className="mt-1 text-sm text-slate-500">A decision map of recent candidates by match strength.</p>
                    </div>
                    <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                      {jobScans.length} recent scored CVs
                    </span>
                  </div>

                  <div className="grid gap-4 lg:grid-cols-3">
                    {radarColumns.map((column) => (
                      <div key={column.label} className={`rounded-lg border ${column.border} ${column.bg} p-4`}>
                        <div className="mb-4 flex items-start justify-between gap-3">
                          <div>
                            <p className="font-semibold text-slate-950">{column.label}</p>
                            <p className="mt-1 text-xs font-medium text-slate-500">{column.helper}</p>
                          </div>
                          <span className={`mt-1 h-3 w-3 rounded-full ${column.dot}`} />
                        </div>
                        <div className="space-y-2">
                          {column.scans.slice(0, 4).map((scan) => (
                            <button
                              key={`${column.label}-${scan.scan_id}`}
                              type="button"
                              onClick={() => handleOpenCandidate(scan)}
                              className="w-full rounded-lg border border-white/70 bg-white p-3 text-left shadow-sm transition hover:border-slate-300"
                            >
                              <div className="grid grid-cols-[minmax(0,1fr)_56px] items-center gap-2">
                                <span className="truncate text-sm font-semibold text-slate-800">{scan.file_name}</span>
                                <span className="text-right text-sm font-semibold tabular-nums text-slate-950">{scan.match_score}%</span>
                              </div>
                              <p className="mt-1 truncate text-xs text-slate-500">{getJobLabel(scan)}</p>
                            </button>
                          ))}
                          {column.scans.length === 0 && (
                            <div className="rounded-lg border border-dashed border-white/80 bg-white/60 p-4 text-center text-sm text-slate-500">
                              No candidates here yet.
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {renderRecruiterCriteriaImpactPanel()}
                {false && (
                <section className="flex h-full flex-col rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
                  <div className="mb-4">
                    <h3 className="text-xl font-semibold tracking-tight text-slate-950">
                      Top Candidates
                    </h3>
                    <p className="mt-1 text-sm text-slate-500">Highest scoring candidates across recent scans</p>
                  </div>

                  <div className="flex flex-1 flex-col gap-3 overflow-y-auto pr-1">
                    {Array.from(
                      new Map((topCandidates || []).map(c => [c.scan_id, c])).values()
                    ).map((c, index) => {
                      const candidateJobs = Array.from(candidateJobsMap[c.id] || []);

                      return (
                        <div
                          key={c.scan_id}
                          className={`flex items-start gap-3 rounded-lg border p-3 transition hover:border-slate-300 hover:shadow-sm ${
                            c.is_shortlisted
                              ? "border-emerald-300 bg-emerald-50"
                              : "border-slate-200 bg-white"
                          }`}
                        >
                          <div
                            className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-bold ${
                              index === 0
                                ? "bg-emerald-800 text-white"
                                : index === 1
                                ? "bg-slate-200 text-slate-800"
                                : index === 2
                                ? "bg-sky-100 text-sky-800"
                                : "bg-slate-100 text-slate-700"
                            }`}
                          >
                            #{index + 1}
                          </div>

                          <div className="min-w-0 flex-1">
                            <div className="mb-1 grid grid-cols-[minmax(0,1fr)_64px] items-center gap-3">
                              <span className="truncate font-semibold text-slate-800">
                                {c.file_name?.replace(".pdf", "").replace(/_/g, " ") || "Candidate"}
                              </span>
                              <span className="text-right font-semibold tabular-nums text-slate-950">
                                {c.match_score}%
                              </span>
                            </div>
                            <div className="mb-2 h-2 overflow-hidden rounded-full bg-slate-100">
                              <div
                                className={`h-full rounded-full ${getScoreBarClass(c.match_score)}`}
                                style={{ width: `${Math.max(0, Math.min(100, c.match_score || 0))}%` }}
                              />
                            </div>

                            {candidateJobs.length > 0 && (
                              <div className="mb-1 truncate text-xs text-slate-500">
                                <span className="font-medium text-slate-600">Roles: </span>
                                {candidateJobs.join(", ")}
                              </div>
                            )}

                            {c.matching_skills && c.matching_skills.length > 0 && (
                              <div className="mt-1 flex flex-wrap gap-1">
                                {c.matching_skills.slice(0, 4).map((matching_skills, idx) => (
                                  <span
                                    key={idx}
                                    className="rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs text-slate-700"
                                  >
                                    {matching_skills}
                                  </span>
                                ))}
                              </div>
                            )}

                            {c.is_shortlisted && (
                              <span className="mt-2 inline-block text-xs font-semibold text-emerald-700">
                                Shortlisted
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {(!topCandidates || topCandidates.length === 0) && (
                      <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-4 text-center text-sm text-slate-500">
                        No candidates available yet.
                      </p>
                    )}
                  </div>
                </section>
                )}
              </div>
              </DashboardLayer>

              <DashboardLayer
                eyebrow="Jobs"
                title="Jobs and criteria"
                description="Keep job-level health, workflow progress, and recruiter criteria pressure together."
              >
              <div className="grid items-stretch gap-6 xl:grid-cols-[1fr_0.9fr]">
                <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                  <div className="border-b border-slate-100 p-5">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div>
                        <h3 className="flex items-center gap-2 text-xl font-semibold tracking-tight text-slate-950">
                          Job Decision Board
                          <MetricInfo text="Shows selected, rejected, and still-undecided candidates for each job so recruiters can continue decisions from the dashboard." />
                        </h3>
                        <p className="mt-1 text-sm text-slate-500">Quickly see who is selected, who is rejected, and who still needs a recruiter decision.</p>
                      </div>
                      {jobHealthCards.length > 0 && (
                        <select
                          value={selectedDashboardJobValue}
                          onChange={(event) => setSelectedDashboardJobName(event.target.value)}
                          className="min-h-[36px] w-full rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-800 outline-none transition focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 sm:w-64"
                        >
                          {jobHealthCards.map((job) => (
                            <option key={job.jobName} value={job.jobName}>
                              {job.jobName}
                            </option>
                          ))}
                        </select>
                      )}
                    </div>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {jobHealthCards.length > 0 && (
                      <div className="space-y-3 p-3">
                        {selectedJobHealthCards.map((job) => (
                          <div key={`job-decision-clean-${job.jobName}`} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                            <div className="flex flex-col gap-3 border-b border-slate-100 pb-2.5 lg:flex-row lg:items-start lg:justify-between">
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-2">
                                  <h4 className="truncate text-base font-semibold text-slate-950">{job.jobName}</h4>
                                  <span className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                                    job.risk === "Low"
                                      ? "bg-emerald-100 text-emerald-800"
                                      : job.risk === "Medium"
                                      ? "bg-amber-100 text-amber-800"
                                      : "bg-rose-100 text-rose-800"
                                  }`}>
                                    {job.risk}
                                  </span>
                                </div>
                                <div className="mt-2 flex flex-wrap gap-1">
                                  <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-700">{job.applicants} CVs</span>
                                  <span className="rounded-md bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-700">{job.avgScore}% avg</span>
                                  <span className="rounded-md bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700">{job.shortlistedCount} selected</span>
                                  <span className="rounded-md bg-rose-50 px-1.5 py-0.5 text-[11px] font-semibold text-rose-700">{job.rejectedCount} rejected</span>
                                  <span className="rounded-md bg-sky-50 px-1.5 py-0.5 text-[11px] font-semibold text-sky-700">{job.reviewCandidates.length} review</span>
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={() => job.jobId && navigate(`/jobs/${job.jobId}/rankings`)}
                                className="inline-flex w-fit items-center justify-center rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 transition hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800"
                              >
                                Open rankings
                              </button>
                            </div>

                            <div className="mt-3 grid gap-2.5 xl:grid-cols-[0.86fr_1.14fr]">
                              <div className="rounded-lg bg-slate-50 p-2.5">
                                <div className="mb-2 flex items-center justify-between">
                                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Selected / Rejected</p>
                                  <span className="text-[11px] font-semibold text-slate-400">click to add note or change</span>
                                </div>
                                <div className="space-y-2">
                                  {(() => {
                                    const decisionCandidates = [
                                      ...job.shortlistedCandidates.map(scan => ({ ...scan, decisionType: "Selected" })),
                                      ...job.rejectedCandidates.map(scan => ({ ...scan, decisionType: "Rejected" })),
                                    ];
                                    const pagedDecisions = paginateItems(decisionCandidates, decisionCandidatesPage, 4);

                                    return (
                                      <>
                                        <div className="flex flex-wrap gap-1.5">
                                          {pagedDecisions.items.map((scan) => {
                                            const isSelected = scan.decisionType === "Selected";

                                            return (
                                              <button
                                                key={`${scan.decisionType}-${scan.scan_id}`}
                                                type="button"
                                                onClick={() => handleOpenCandidate(scan)}
                                                className={`max-w-full rounded-full border bg-white px-2 py-0.5 text-[11px] font-semibold transition ${
                                                  isSelected
                                                    ? "border-emerald-200 text-emerald-800 hover:bg-emerald-50"
                                                    : "border-rose-200 text-rose-800 hover:bg-rose-50"
                                                }`}
                                              >
                                                <span className="mr-1">{scan.decisionType}</span>
                                                <span className="inline-block max-w-[128px] truncate align-bottom text-slate-800">{scan.file_name}</span>
                                              </button>
                                            );
                                          })}
                                        </div>
                                        {decisionCandidates.length === 0 && (
                                          <p className="rounded-lg border border-dashed border-slate-200 bg-white p-2.5 text-xs text-slate-500">
                                            No selected or rejected candidates yet.
                                          </p>
                                        )}
                                        <PaginationControls
                                          page={pagedDecisions.page}
                                          totalPages={pagedDecisions.totalPages}
                                          onChange={setDecisionCandidatesPage}
                                        />
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>

                              <div className="rounded-lg bg-slate-50 p-2.5">
                                <div className="mb-2 flex items-center justify-between">
                                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Needs review</p>
                                  <span className="rounded-full bg-white px-2 py-0.5 text-[11px] font-bold text-slate-700">{job.reviewCandidates.length}</span>
                                </div>
                                <div className="space-y-2">
                                  {(() => {
                                    const pagedReviews = paginateItems(job.reviewCandidates, reviewCandidatesPage, 3);

                                    return (
                                      <>
                                        {pagedReviews.items.map((scan) => (
                                          <div key={`review-clean-${scan.scan_id}`} className="grid grid-cols-[minmax(0,1fr)_42px] gap-1.5 rounded-lg border border-slate-200 bg-white p-1.5 sm:grid-cols-[minmax(0,1fr)_42px_62px_58px] sm:items-center">
                                            <button
                                              type="button"
                                              onClick={() => handleOpenCandidate(scan)}
                                              className="truncate text-left text-xs font-semibold text-slate-900"
                                            >
                                              {scan.file_name}
                                            </button>
                                            <span className="text-right text-xs font-bold tabular-nums text-slate-800">{scan.match_score}%</span>
                                            <button
                                              type="button"
                                              onClick={() => handleQuickShortlist(scan)}
                                              className="rounded-md border border-emerald-200 bg-emerald-50 px-1.5 py-1 text-[11px] font-semibold text-emerald-800 transition hover:bg-emerald-100"
                                            >
                                              Select
                                            </button>
                                            <button
                                              type="button"
                                              onClick={() => handleQuickReject(scan)}
                                              className="rounded-md border border-rose-200 bg-rose-50 px-1.5 py-1 text-[11px] font-semibold text-rose-800 transition hover:bg-rose-100"
                                            >
                                              Reject
                                            </button>
                                          </div>
                                        ))}
                                        {job.reviewCandidates.length === 0 && (
                                          <p className="rounded-lg border border-dashed border-slate-200 bg-white p-3 text-xs text-slate-500">
                                            All candidates have a decision.
                                          </p>
                                        )}
                                        <PaginationControls
                                          page={pagedReviews.page}
                                          totalPages={pagedReviews.totalPages}
                                          onChange={setReviewCandidatesPage}
                                        />
                                      </>
                                    );
                                  })()}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {false && jobHealthCards.length > 0 && (
                      <div className="mx-4 my-4 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
                        <div className="hidden grid-cols-[minmax(0,1.2fr)_96px_minmax(180px,1fr)_minmax(220px,1.2fr)_90px_40px] items-center gap-4 border-b border-slate-100 bg-slate-50 px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400 lg:grid">
                          <span>Job</span>
                          <span>Health</span>
                          <span>Selected / Rejected</span>
                          <span>Needs review</span>
                          <span>Candidates</span>
                          <span />
                        </div>

                        <div className="divide-y divide-slate-100">
                          {jobHealthCards.map((job) => (
                            <div
                              key={`job-row-${job.jobName}`}
                              className="grid gap-4 px-4 py-4 transition hover:bg-emerald-50/30 lg:grid-cols-[minmax(0,1.2fr)_96px_minmax(180px,1fr)_minmax(220px,1.2fr)_90px_40px] lg:items-center"
                            >
                              <div className="min-w-0">
                                <p className="truncate text-sm font-semibold text-slate-950">{job.jobName}</p>
                                <p className="mt-1 text-xs text-slate-500">
                                  Latest {job.latestActivityAt ? new Date(job.latestActivityAt).toLocaleDateString() : "No scan yet"}
                                </p>
                              </div>

                              <div className="flex items-center gap-2">
                                <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-4 ${
                                  job.risk === "Low"
                                    ? "border-emerald-500 text-emerald-700"
                                    : job.risk === "Medium"
                                    ? "border-amber-400 text-amber-700"
                                    : "border-rose-400 text-rose-700"
                                }`}>
                                  <span className="text-sm font-bold">{job.avgScore}</span>
                                </div>
                                <span className={`rounded-full px-2 py-1 text-[11px] font-semibold ${
                                  job.risk === "Low"
                                    ? "bg-emerald-100 text-emerald-800"
                                    : job.risk === "Medium"
                                    ? "bg-amber-100 text-amber-800"
                                    : "bg-rose-100 text-rose-800"
                                }`}>
                                  {job.risk}
                                </span>
                              </div>

                              <div className="grid gap-2">
                                <button
                                  type="button"
                                  disabled={!job.shortlistedCandidates[0]}
                                  onClick={() => job.shortlistedCandidates[0] && handleOpenCandidate(job.shortlistedCandidates[0])}
                                  className="grid grid-cols-[58px_minmax(0,1fr)_42px] items-center gap-2 rounded-lg border border-emerald-100 bg-emerald-50/70 px-2 py-1.5 text-left disabled:cursor-default disabled:opacity-70"
                                >
                                  <span className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">Selected</span>
                                  <span className="truncate text-xs font-semibold text-slate-900">
                                    {job.shortlistedCandidates[0]?.file_name || "None yet"}
                                  </span>
                                  <span className="text-right text-xs font-bold text-emerald-800">{job.shortlistedCount}</span>
                                </button>
                                <button
                                  type="button"
                                  disabled={!job.rejectedCandidates[0]}
                                  onClick={() => job.rejectedCandidates[0] && handleOpenCandidate(job.rejectedCandidates[0])}
                                  className="grid grid-cols-[58px_minmax(0,1fr)_42px] items-center gap-2 rounded-lg border border-rose-100 bg-rose-50/70 px-2 py-1.5 text-left disabled:cursor-default disabled:opacity-70"
                                >
                                  <span className="text-[11px] font-bold uppercase tracking-wide text-rose-700">Rejected</span>
                                  <span className="truncate text-xs font-semibold text-slate-900">
                                    {job.rejectedCandidates[0]?.file_name || "None yet"}
                                  </span>
                                  <span className="text-right text-xs font-bold text-rose-800">{job.rejectedCount}</span>
                                </button>
                              </div>

                              <div className="space-y-2">
                                {job.reviewCandidates.slice(0, 2).map((scan) => (
                                  <div key={`review-row-${scan.scan_id}`} className="grid grid-cols-[minmax(0,1fr)_44px_68px_62px] items-center gap-2 rounded-lg border border-slate-100 bg-slate-50 px-2 py-1.5">
                                    <button
                                      type="button"
                                      onClick={() => handleOpenCandidate(scan)}
                                      className="truncate text-left text-xs font-semibold text-slate-900"
                                    >
                                      {scan.file_name}
                                    </button>
                                    <span className="text-right text-xs font-bold tabular-nums text-slate-800">{scan.match_score}%</span>
                                    <button
                                      type="button"
                                      onClick={() => handleQuickShortlist(scan)}
                                      className="rounded-md border border-emerald-200 bg-white px-2 py-1 text-[11px] font-semibold text-emerald-800 transition hover:bg-emerald-50"
                                    >
                                      Select
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleQuickReject(scan)}
                                      className="rounded-md border border-rose-200 bg-white px-2 py-1 text-[11px] font-semibold text-rose-800 transition hover:bg-rose-50"
                                    >
                                      Reject
                                    </button>
                                  </div>
                                ))}
                                {job.reviewCandidates.length === 0 && (
                                  <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 px-2 py-2 text-xs text-slate-500">
                                    All candidates have a decision.
                                  </p>
                                )}
                              </div>

                              <div className="text-sm font-semibold text-slate-900">
                                {job.applicants}
                                <span className="ml-1 text-xs font-medium text-slate-500">CVs</span>
                              </div>

                              <button
                                type="button"
                                onClick={() => job.jobId && navigate(`/jobs/${job.jobId}/rankings`)}
                                className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-lg text-slate-400 transition hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-700"
                                aria-label={`Open rankings for ${job.jobName}`}
                              >
                                &rsaquo;
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {false && jobHealthCards.map((job) => (
                      <div
                        key={job.jobName}
                        className="hidden"
                      >
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-slate-950">{job.jobName}</p>
                          <p className="mt-1 text-xs text-slate-500">
                            Best match: <span className="font-medium text-slate-700">{job.bestMatchName}</span>
                          </p>
                          <p className="mt-1 text-xs text-slate-500">
                            {job.shortlistedCount} shortlisted from {job.applicants} scanned
                          </p>
                        </div>

                        <div className="flex items-center gap-2">
                          <div className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full border-4 ${
                            job.risk === "Low"
                              ? "border-emerald-500 text-emerald-700"
                              : job.risk === "Medium"
                              ? "border-amber-400 text-amber-700"
                              : "border-rose-400 text-rose-700"
                          }`}>
                            <span className="text-sm font-bold">{job.avgScore}</span>
                          </div>
                          <span className={`rounded-full px-2 py-1 text-[11px] font-semibold ${
                            job.risk === "Low"
                              ? "bg-emerald-100 text-emerald-800"
                              : job.risk === "Medium"
                              ? "bg-amber-100 text-amber-800"
                              : "bg-rose-100 text-rose-800"
                          }`}>
                            {job.risk}
                          </span>
                        </div>

                        <div>
                          <span className={`inline-flex max-w-full rounded-full px-2.5 py-1 text-xs font-semibold ${
                            job.topImpact === "Criteria needed"
                              ? "bg-rose-50 text-rose-700"
                              : job.risk === "Low"
                              ? "bg-emerald-50 text-emerald-700"
                              : "bg-amber-50 text-amber-700"
                          }`}>
                            <span className="truncate">{job.topImpact}</span>
                          </span>
                        </div>

                        <div className="text-xs font-medium text-slate-500">
                          {job.latestActivityAt ? new Date(job.latestActivityAt).toLocaleDateString() : "No scan yet"}
                        </div>

                        <div className="text-sm font-semibold text-slate-900">
                          {job.applicants}
                          <span className="ml-1 text-xs font-medium text-slate-500">CVs</span>
                        </div>

                        <span className="hidden text-right text-xl text-slate-300 lg:block">›</span>
                      </div>
                    ))}
                    {false && jobHealthCards.map((job) => (
                      <div key={`decision-${job.jobName}`} className="mx-4 my-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
                        <div className="mb-3 grid gap-3 rounded-lg border border-slate-200 bg-slate-50/70 p-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <h4 className="truncate text-base font-semibold text-slate-950">{job.jobName}</h4>
                              <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${
                                job.risk === "Low"
                                  ? "bg-emerald-100 text-emerald-800"
                                  : job.risk === "Medium"
                                  ? "bg-amber-100 text-amber-800"
                                  : "bg-rose-100 text-rose-800"
                              }`}>
                                {job.risk} risk
                              </span>
                            </div>
                            <div className="mt-2 flex flex-wrap gap-1.5">
                              <span className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600">{job.applicants} scanned</span>
                              <span className="rounded-md border border-slate-200 bg-white px-2 py-1 text-[11px] font-semibold text-slate-600">{job.avgScore}% avg</span>
                              <span className="rounded-md border border-emerald-100 bg-emerald-50 px-2 py-1 text-[11px] font-semibold text-emerald-700">{job.shortlistedCount} selected</span>
                              <span className="rounded-md border border-rose-100 bg-rose-50 px-2 py-1 text-[11px] font-semibold text-rose-700">{job.rejectedCount} rejected</span>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => job.jobId && navigate(`/jobs/${job.jobId}/rankings`)}
                            className="inline-flex w-fit items-center justify-center rounded-lg border border-emerald-200 bg-white px-3 py-2 text-xs font-semibold text-emerald-800 transition hover:border-emerald-300 hover:bg-emerald-50"
                          >
                            Open rankings
                          </button>
                        </div>
                        <div className="grid gap-3 xl:grid-cols-[0.8fr_1.2fr]">
                          <div className="rounded-lg border border-slate-200 bg-white p-3">
                            <div className="mb-3 flex items-center justify-between border-b border-slate-100 pb-2">
                              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Decisions</p>
                              <div className="flex gap-1.5">
                                <span className="rounded-md bg-emerald-50 px-1.5 py-0.5 text-[11px] font-bold text-emerald-800">{job.shortlistedCount}</span>
                                <span className="rounded-md bg-rose-50 px-1.5 py-0.5 text-[11px] font-bold text-rose-800">{job.rejectedCount}</span>
                              </div>
                            </div>
                            <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-1">
                              <div>
                                <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-emerald-700">Selected</p>
                                {job.shortlistedCandidates.slice(0, 1).map((scan) => (
                                  <button
                                    key={`selected-${scan.scan_id}`}
                                    type="button"
                                    onClick={() => handleOpenCandidate(scan)}
                                    className="w-full rounded-lg border border-slate-100 bg-slate-50/70 p-2 text-left transition hover:border-emerald-300 hover:bg-emerald-50"
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      <span className="truncate text-xs font-semibold text-slate-900">{scan.file_name}</span>
                                      <span className="shrink-0 rounded-full bg-emerald-100 px-1.5 py-0.5 text-[11px] font-bold text-emerald-800">{scan.match_score}%</span>
                                    </div>
                                  </button>
                                ))}
                                {job.shortlistedCandidates.length === 0 && (
                                  <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-2 text-xs text-slate-500">None yet</p>
                                )}
                              </div>
                              <div>
                                <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-rose-700">Rejected</p>
                                {job.rejectedCandidates.slice(0, 1).map((scan) => (
                                  <button
                                    key={`rejected-${scan.scan_id}`}
                                    type="button"
                                    onClick={() => handleOpenCandidate(scan)}
                                    className="w-full rounded-lg border border-slate-100 bg-slate-50/70 p-2 text-left transition hover:border-rose-300 hover:bg-rose-50"
                                  >
                                    <div className="flex items-center justify-between gap-2">
                                      <span className="truncate text-xs font-semibold text-slate-900">{scan.file_name}</span>
                                      <span className="shrink-0 rounded-full bg-rose-100 px-1.5 py-0.5 text-[11px] font-bold text-rose-800">{scan.match_score}%</span>
                                    </div>
                                  </button>
                                ))}
                                {job.rejectedCandidates.length === 0 && (
                                  <p className="rounded-lg border border-dashed border-slate-200 bg-slate-50 p-2 text-xs text-slate-500">None yet</p>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="rounded-lg border border-slate-200 bg-white p-3">
                            <div className="mb-3 flex items-center justify-between border-b border-slate-100 pb-2">
                              <p className="text-xs font-semibold uppercase tracking-wide text-sky-700">Needs review</p>
                              <span className="rounded-full bg-sky-50 px-2 py-1 text-xs font-bold text-sky-800">{job.reviewCandidates.length}</span>
                            </div>
                            <div className="space-y-2">
                              {job.reviewCandidates.slice(0, 3).map((scan) => (
                                <div key={`review-${scan.scan_id}`} className="rounded-lg border border-slate-100 bg-slate-50/70 p-2.5">
                                  <button
                                    type="button"
                                    onClick={() => handleOpenCandidate(scan)}
                                    className="grid w-full grid-cols-[minmax(0,1fr)_48px] items-center gap-2 text-left"
                                  >
                                    <span className="truncate text-sm font-semibold text-slate-900">{scan.file_name}</span>
                                    <span className="text-right text-xs font-bold tabular-nums text-slate-800">{scan.match_score}%</span>
                                  </button>
                                  <div className="mt-2 grid grid-cols-2 gap-2">
                                    <button
                                      type="button"
                                      onClick={() => handleQuickShortlist(scan)}
                                      className="rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-xs font-semibold text-emerald-800 transition hover:bg-emerald-100"
                                    >
                                      Select
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => handleQuickReject(scan)}
                                      className="rounded-md border border-rose-200 bg-rose-50 px-2 py-1.5 text-xs font-semibold text-rose-800 transition hover:bg-rose-100"
                                    >
                                      Reject
                                    </button>
                                  </div>
                                </div>
                              ))}
                              {job.reviewCandidates.length === 0 && (
                                <p className="rounded-lg border border-dashed border-slate-200 bg-white p-3 text-sm text-slate-500">All scanned candidates have a decision.</p>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                    {jobHealthCards.length === 0 && (
                      <div className="p-6 text-center text-sm text-slate-500">
                        Scan candidates against a job to build job health signals.
                      </div>
                    )}
                  </div>
                </section>

                {renderTopCandidatesPanel()}
                {false && (
                <section className="flex h-full flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                  <div className="border-b border-slate-100 p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
                          Criteria impact
                        </p>
                        <h3 className="mt-1.5 flex items-center gap-2 text-lg font-semibold tracking-tight text-slate-950">
                          <span>Recruiter Criteria Impact</span>
                          <MetricInfo text="This explains how job requirements are affecting scan results, using repeated missing skills, candidate gap pressure, and job-level gap signals." />
                        </h3>
                        <p className="mt-1 text-sm leading-5 text-slate-500">
                          See which requirements are shaping rankings before changing the JD or sourcing more candidates.
                        </p>
                      </div>
                      <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-600">
                        Explainable
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-1 p-4 sm:p-5">
                    <div className="grid flex-1 gap-2.5">
                      {recruiterCriteriaImpact.map((item) => (
                        <div
                          key={item.label}
                          className={`flex flex-col justify-between rounded-lg border px-3 py-2.5 text-left ${item.tone}`}
                        >
                          <div className="flex items-start justify-between gap-3">
                            <div className="min-w-0 flex-1">
                              <p className="text-xs font-semibold uppercase tracking-wide opacity-70">{item.label}</p>
                              <p className="mt-1 text-sm leading-5 opacity-85">{item.detail}</p>
                            </div>
                            <p className="shrink-0 rounded-md border border-current/10 bg-white/60 px-2.5 py-1 text-sm font-bold tabular-nums">
                              {item.value}
                            </p>
                          </div>
                          <p className="mt-2 rounded-md border border-current/10 bg-white/55 px-2.5 py-1.5 text-xs leading-5 opacity-80">
                            {item.helper}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </section>
                )}
              </div>
              </DashboardLayer>

              <DashboardLayer
                eyebrow="Candidates"
                title="Candidates and activity"
                description="Review recent job candidate lists, top candidates, and wider scan activity charts."
              >
              <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
                
                {selectedCandidate && (
                    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 px-4">
                      <div className="bg-white p-6 rounded-lg w-full max-w-[500px] max-h-[90vh] overflow-y-auto shadow-lg">
                        
                        <h2 className="text-lg font-bold mb-2">
                          {selectedCandidate.file_name}
                        </h2>

                        <p className="text-sm text-gray-500 mb-2">
                          Match Score: {selectedCandidate.match_score}%
                        </p>

                        <textarea
                          className="w-full border rounded p-2 mt-2"
                          rows={4}
                          placeholder="Add recruiter note..."
                          value={note}
                          onChange={(e) => setNote(e.target.value)}
                        />

                        <div className="flex justify-end gap-2 mt-4">
                          <div className="flex justify-between items-center mt-4">

                            {/* Shortlist toggle */}
                            <button
                              onClick={handleToggleShortlist}
                              className={`px-3 py-1 rounded text-white font-semibold ${
                                selectedCandidate.is_shortlisted
                                  ? "bg-red-500 hover:bg-red-600"
                                  : "bg-green-600 hover:bg-green-700"
                              }`}
                            >
                              {selectedCandidate.is_shortlisted
                                ? "Remove Shortlist"
                                : selectedCandidate.is_rejected
                                ? "Select again"
                                : "Shortlist"}
                            </button>

                            {/* Right side buttons */}
                            <div className="flex gap-2">
                              <button
                                onClick={() => setSelectedCandidate(null)}
                                className="px-3 py-1 bg-gray-300 rounded"
                              >
                                Cancel
                              </button>

                              <button
                                onClick={handleSaveNote}
                                className="px-3 py-1 bg-indigo-600 text-white rounded"
                              >
                                Save
                              </button>
                            </div>

                          </div>
                        </div>

                      </div>
                    </div>
                  )}

                {false && (
                <section className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 md:col-span-12">
                  <div className="mb-4">
                    <h3 className="text-xl font-semibold tracking-tight text-slate-950">
                      Top Candidates
                    </h3>
                    <p className="text-sm text-slate-500 mt-1">Highest scoring candidates across recent scans</p>
                  </div>

                  <div className="flex flex-col gap-4 max-h-[32rem] overflow-y-auto">
                    {Array.from(
                      new Map((topCandidates || []).map(c => [c.scan_id, c])).values()
                    ).map((c, index) => {
                      const candidateJobs = Array.from(candidateJobsMap[c.id] || []);

                      return (
                        <div
                          key={c.scan_id}
                          className={`flex items-start gap-4 p-4 border rounded-lg transition hover:border-slate-300 hover:shadow-sm ${
                            c.is_shortlisted
                              ? "bg-emerald-50 border-emerald-300"
                              : "bg-white border-slate-200"
                          }`}
                        >
                          {/* Ranking Badge */}
                          <div
                            className={`w-8 h-8 flex items-center justify-center rounded-full font-bold text-sm ${
                              index === 0
                                ? "bg-emerald-800 text-white"
                                : index === 1
                                ? "bg-slate-200 text-slate-800"
                                : index === 2
                                ? "bg-sky-100 text-sky-800"
                                : "bg-slate-100 text-slate-700"
                            }`}
                          >
                            #{index + 1}
                          </div>

                          {/* Candidate Info */}
                          <div className="flex-1">
                            <div className="grid grid-cols-[minmax(0,1fr)_72px] items-center gap-3 mb-1">
                              {/* Candidate Name */}
                              <span className="truncate font-semibold text-slate-800">
                                {c.file_name?.replace(".pdf", "").replace(/_/g, " ") || "Candidate"}
                              </span>

                              {/* Match Score */}
                              <span className="text-right font-semibold tabular-nums text-slate-950">
                                {c.match_score}%
                              </span>
                            </div>
                            <div className="mb-3 h-2 overflow-hidden rounded-full bg-slate-100">
                              <div
                                className={`h-full rounded-full ${getScoreBarClass(c.match_score)}`}
                                style={{ width: `${Math.max(0, Math.min(100, c.match_score || 0))}%` }}
                              />
                            </div>

                            {/* Jobs Scanned For */}
                            {candidateJobs.length > 0 && (
                              <div className="text-gray-500 text-sm mb-1">
                                <span className="font-medium text-gray-600">Scanned for roles: </span>
                                {candidateJobs.join(", ")}
                              </div>
                            )}

                            {/* Top Skills */}
                            {c.matching_skills && c.matching_skills.length > 0 && (
                              <div className="flex flex-wrap gap-1 mt-1">
                                <span className="font-medium text-gray-600 text-xs w-full">
                                  Top matched skills:
                                </span>
                                {c.matching_skills.slice(0, 5).map((matching_skills, idx) => (
                                  <span
                                    key={idx}
                                    className="bg-slate-100 text-slate-700 border border-slate-200 px-2 py-0.5 rounded-full text-xs"
                                  >
                                    {matching_skills}
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* Shortlisted Badge */}
                            {c.is_shortlisted && (
                              <span className="text-green-700 text-xs font-semibold mt-2 inline-block">
                                ✓ Shortlisted
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* Empty State */}
                    {(!topCandidates || topCandidates.length === 0) && (
                      <p className="text-gray-400 text-center">
                        No candidates available yet.
                      </p>
                    )}
                  </div>
                </section>
                )}

                {/* PANEL 4: Charts */}
                <section className="bg-white rounded-lg border border-slate-200 shadow-sm p-6 md:col-span-12">
                  <div className="mb-5 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                      <p className="mb-2 inline-flex rounded-full bg-emerald-50 px-3 py-1 text-xs font-bold uppercase tracking-wide text-emerald-800">
                        Activity
                      </p>
                      <h3 className="text-xl font-semibold tracking-tight text-slate-950">Candidate Analysis</h3>
                      <p className="mt-1 text-sm text-slate-500">
                        Recruiter-friendly view of shortlist quality, job volume, and match strength.
                      </p>
                    </div>
                    <span className="inline-flex self-start rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
                      Live scan data
                    </span>
                  </div>

                  <div className="grid gap-5 xl:grid-cols-[0.9fr_1.1fr]">
                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-5">
                      <div className="mb-3 flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-800">Shortlist quality</p>
                          <p className="mt-1 text-xs text-slate-500">How many reviewed CVs moved into the shortlist.</p>
                        </div>
                        <span className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-slate-600 shadow-sm">
                          {shortlistedRate}% rate
                        </span>
                      </div>

                      <div className="relative h-60">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={pieData}
                              dataKey="value"
                              nameKey="name"
                              innerRadius={64}
                              outerRadius={88}
                              paddingAngle={3}
                              stroke="none"
                            >
                              <Cell key="shortlisted" fill="#059669" />
                              <Cell key="needsReview" fill="#d97706" />
                            </Pie>
                            <Tooltip
                              formatter={(value, name) => [`${value} candidates`, name]}
                              contentStyle={{
                                borderRadius: "10px",
                                border: "1px solid #e2e8f0",
                                boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
                              }}
                            />
                          </PieChart>
                        </ResponsiveContainer>
                        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
                          <span className="text-3xl font-semibold tabular-nums text-slate-950">
                            {dashboardStats.shortlisted}
                          </span>
                          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                            shortlisted
                          </span>
                        </div>
                      </div>

                      <div className="mt-3 flex flex-wrap gap-2">
                        <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600">
                          <span className="h-2 w-2 rounded-full bg-emerald-600" />
                          Shortlisted
                        </span>
                        <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-600">
                          <span className="h-2 w-2 rounded-full bg-amber-600" />
                          Needs review
                        </span>
                      </div>
                    </div>

                    <div className="rounded-lg border border-slate-200 bg-slate-50 p-5">
                      <div className="mb-3 flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-slate-800">Candidates by job</p>
                          <p className="mt-1 text-xs text-slate-500">Horizontal bars keep long job names readable.</p>
                        </div>
                        <span className="rounded-full bg-white px-2 py-1 text-xs font-semibold text-slate-600 shadow-sm">
                          Top {barData.length || 0}
                        </span>
                      </div>

                      <div className="h-72">
                        {barData.length === 0 ? (
                          <div className="flex h-full items-center justify-center rounded-lg border border-dashed border-slate-200 bg-white text-sm text-slate-400">
                            No job scan data yet.
                          </div>
                        ) : (
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={barData}
                              layout="vertical"
                              margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                            >
                              <XAxis type="number" hide />
                              <YAxis
                                type="category"
                                dataKey="displayJob"
                                width={132}
                                axisLine={false}
                                tickLine={false}
                                tick={{ fontSize: 11, fill: "#64748b" }}
                              />
                              <Tooltip
                                formatter={(value, name) => [
                                  `${value} candidates`,
                                  name === "shortlisted" ? "Shortlisted" : "Needs review",
                                ]}
                                labelFormatter={(label) => (
                                  barData.find(item => item.displayJob === label)?.job || label
                                )}
                                contentStyle={{
                                  borderRadius: "10px",
                                  border: "1px solid #e2e8f0",
                                  boxShadow: "0 10px 30px rgba(15,23,42,0.08)",
                                }}
                              />
                              <Bar dataKey="shortlisted" stackId="job" fill="#059669" radius={[0, 6, 6, 0]} />
                              <Bar dataKey="nonSelected" stackId="job" fill="#f59e0b" radius={[0, 6, 6, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-5 rounded-lg border border-slate-200 bg-slate-50 p-5">
                    <div className="mb-4 flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between">
                      <div>
                        <p className="text-sm font-semibold text-slate-800">Match quality distribution</p>
                        <p className="mt-1 text-xs text-slate-500">Quickly see whether the talent pool is strong, mixed, or weak for current jobs.</p>
                      </div>
                      <span className="text-xs font-semibold text-slate-500">
                        {jobScans.length} scored results
                      </span>
                    </div>

                    <div className="grid gap-3 md:grid-cols-3">
                      {matchQualityData.map((item) => {
                        const percent = jobScans.length
                          ? Math.round((item.value / jobScans.length) * 100)
                          : 0;

                        return (
                          <div key={item.label} className="rounded-lg border border-slate-200 bg-white p-4">
                            <div className="mb-3 flex items-start justify-between gap-3">
                              <div>
                                <p className={`text-sm font-semibold ${item.text}`}>{item.label}</p>
                                <p className="mt-1 text-xs text-slate-500">{item.helper}</p>
                              </div>
                              <span className="text-xl font-semibold tabular-nums text-slate-950">
                                {item.value}
                              </span>
                            </div>
                            <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                              <div
                                className={`h-full rounded-full ${item.bar}`}
                                style={{ width: `${percent}%` }}
                              />
                            </div>
                            <p className="mt-2 text-xs font-medium text-slate-500">{percent}% of scored CVs</p>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </section>

              </div>
              </DashboardLayer>
            </div>
          )}

          {activeAction === "single" && <SingleCVScan user={user} onScanConsumed={handleScanConsumed} />}
          {activeAction === "candidate_search" && <CandidateSearch user={user} />}
          {activeAction === "api" && <ApiAccess user={user} />}
        </main>
        </div>
      </div>
    </div>
  );
}
